import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, Keyboard, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Renders a live camera QR scanner with a manual token fallback.
export default function QRScanner({ onDetected, onCancel }) {
  const [manual, setManual] = useState(false);
  const [token, setToken] = useState("");
  const [camError, setCamError] = useState("");
  const scannerRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (manual) return;
    let cancelled = false;
    const scanner = new Html5Qrcode("qr-reader");
    scannerRef.current = scanner;
    scanner
      .start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        (decodedText) => {
          if (cancelled) return;
          stop().then(() => onDetected(decodedText.trim()));
        },
        () => {}
      )
      .then(() => { startedRef.current = true; })
      .catch(() => setCamError("Tidak dapat mengakses kamera. Gunakan input token manual."));

    return () => { cancelled = true; stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manual]);

  const stop = async () => {
    const s = scannerRef.current;
    if (s && startedRef.current) {
      try { await s.stop(); await s.clear(); } catch (e) {}
      startedRef.current = false;
    }
  };

  const cancel = async () => { await stop(); onCancel(); };
  const submitManual = () => { if (token.trim()) onDetected(token.trim()); };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/95 flex flex-col" data-testid="qr-scanner-modal">
      <div className="flex items-center justify-between px-4 py-4 text-white">
        <span className="font-semibold flex items-center gap-2"><Camera className="h-5 w-5" /> Scan QR Outlet</span>
        <button onClick={cancel} data-testid="qr-close-button" className="p-1 rounded-full hover:bg-white/10">
          <X className="h-6 w-6" />
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6">
        {!manual ? (
          <>
            <div id="qr-reader" className="w-full max-w-xs rounded-2xl overflow-hidden bg-black" />
            {camError && <p className="text-rose-300 text-sm mt-4 text-center">{camError}</p>}
            <p className="text-slate-300 text-sm mt-6 text-center">Arahkan kamera ke QR Code outlet</p>
            <button onClick={() => { stop(); setManual(true); }} data-testid="switch-manual-button"
              className="mt-4 text-sky-300 text-sm flex items-center gap-1.5 underline">
              <Keyboard className="h-4 w-4" /> Input token manual
            </button>
          </>
        ) : (
          <div className="w-full max-w-xs bg-white rounded-2xl p-6 space-y-4">
            <p className="text-sm font-medium text-slate-700">Masukkan token QR outlet</p>
            <Input data-testid="qr-manual-token-input" value={token} onChange={(e) => setToken(e.target.value)}
              placeholder="MLC-xxxxxxxx" />
            <Button data-testid="qr-submit-token-button" onClick={submitManual} className="w-full bg-[#3368A0] hover:bg-[#284f7a]">
              Lanjutkan
            </Button>
            <button onClick={() => setManual(false)} className="w-full text-sm text-slate-500 underline">
              Kembali ke kamera
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
